import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import {
  blankBlockerDraft,
  normalizeImpactDays,
  validateBlockerDraft,
} from '../../features/blockers/blockerModel';
import type { Blocker, BlockerDraft } from '../../features/blockers/types';
import type { GoalSummary } from '../../features/goals/types';
import type { WorkstreamSummary } from '../../features/workstreams/types';
import type { Task } from '../../features/tasks/types';

export interface BlockerModalProps {
  open: boolean;
  onClose: () => void;
  /** Default blocked-engineer alias (the current user). */
  defaultEngineer: string;
  /**
   * Optional pre-filled draft — used when converting a daily-update blocker
   * into a structured Blocker. When provided it seeds the form; the human still
   * verifies + completes it before saving.
   */
  seedDraft?: BlockerDraft;
  /** Goals — populate the affected-goal picker. */
  goals: GoalSummary[];
  /** Workstreams — populate the blocked-workstream picker. */
  workstreams: WorkstreamSummary[];
  /** Tasks — populate the blocked-story picker. */
  tasks: Task[];
  onCreate: (draft: BlockerDraft) => Promise<Blocker>;
  /** Optional title override (e.g. "Convert to structured blocker"). */
  title?: string;
}

const SCOPE_OPTIONS: SelectOption[] = [
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'EXTERNAL', label: 'External' },
];

/**
 * Create / convert a structured Blocker. This is the HUMAN-VERIFY step: whether
 * a blocker was typed fresh or seeded from a daily update, the human confirms
 * the dependency owner, scope, dates, and forecast impact before it lands on
 * the board.
 */
export function BlockerModal({
  open,
  onClose,
  defaultEngineer,
  seedDraft,
  goals,
  workstreams,
  tasks,
  onCreate,
  title = 'New blocker',
}: BlockerModalProps) {
  const [draft, setDraft] = useState<BlockerDraft>(
    seedDraft ?? blankBlockerDraft(defaultEngineer),
  );
  const [impactText, setImpactText] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = seedDraft ?? blankBlockerDraft(defaultEngineer);
    setDraft(base);
    setImpactText(
      base.forecastImpactDays === undefined ? '' : String(base.forecastImpactDays),
    );
    setProblems([]);
  }, [open, seedDraft, defaultEngineer]);

  const goalOptions: SelectOption[] = goals.map((g) => ({
    value: g.id,
    label: g.title || g.id,
  }));
  const workstreamOptions: SelectOption[] = workstreams.map((w) => ({
    value: w.id,
    label: w.title || w.id,
  }));
  const storyOptions: SelectOption[] = tasks.map((t) => ({
    value: t.id,
    label: t.title || t.id,
  }));

  const set = <K extends keyof BlockerDraft>(key: K, value: BlockerDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const buildDraft = (): BlockerDraft => ({
    ...draft,
    summary: draft.summary.trim(),
    blockedEngineer: draft.blockedEngineer.trim(),
    dependencyOwner: draft.dependencyOwner.trim(),
    blockedStoryId: draft.blockedStoryId || undefined,
    blockedWorkstreamId: draft.blockedWorkstreamId || undefined,
    affectedGoalId: draft.affectedGoalId || undefined,
    forecastImpactDays: normalizeImpactDays(impactText),
  });

  const submit = async () => {
    const next = buildDraft();
    const found = validateBlockerDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      await onCreate(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to save blocker.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="lg"
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            Save blocker
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        <Textarea
          label="Summary (required)"
          hint="What is blocked and why."
          rows={2}
          value={draft.summary}
          onChange={(e) => set('summary', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Blocked engineer (required)"
            value={draft.blockedEngineer}
            onChange={(e) => set('blockedEngineer', e.target.value)}
          />
          <Input
            label="Dependency owner (required)"
            placeholder="Who owns the thing we're waiting on"
            value={draft.dependencyOwner}
            onChange={(e) => set('dependencyOwner', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Internal / external"
            options={SCOPE_OPTIONS}
            value={draft.internalExternal}
            onChange={(e) =>
              set('internalExternal', e.target.value as BlockerDraft['internalExternal'])
            }
          />
          <Input
            label="Escalation owner"
            placeholder="Optional"
            value={draft.escalationOwner ?? ''}
            onChange={(e) => set('escalationOwner', e.target.value || undefined)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            type="date"
            label="Blocking since (required)"
            value={draft.blockingSince}
            onChange={(e) => set('blockingSince', e.target.value)}
          />
          <Input
            type="date"
            label="Expected resolution"
            value={draft.expectedResolution ?? ''}
            onChange={(e) => set('expectedResolution', e.target.value || undefined)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            type="number"
            min={0}
            label="Forecast delivery impact (days)"
            hint="Consumed by goal health & forecast views."
            value={impactText}
            onChange={(e) => setImpactText(e.target.value)}
          />
          <Input
            label="Related ticket"
            placeholder="Optional id / URL"
            value={draft.relatedTicket ?? ''}
            onChange={(e) => set('relatedTicket', e.target.value || undefined)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Blocked story"
            placeholder="None"
            value={draft.blockedStoryId ?? ''}
            options={storyOptions}
            onChange={(e) => set('blockedStoryId', e.target.value || undefined)}
          />
          <Select
            label="Blocked workstream"
            placeholder="None"
            value={draft.blockedWorkstreamId ?? ''}
            options={workstreamOptions}
            onChange={(e) => set('blockedWorkstreamId', e.target.value || undefined)}
          />
        </div>

        <Select
          label="Affected goal"
          placeholder="None"
          value={draft.affectedGoalId ?? ''}
          options={goalOptions}
          onChange={(e) => set('affectedGoalId', e.target.value || undefined)}
        />
      </div>
    </Modal>
  );
}

export default BlockerModal;
